import { ReactNode, JSX } from 'react';
import { useApp } from '@backstage/core-plugin-api';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

export type RequireFeatureFlagProps = {
  /**
   * The feature flag key inside the "features" config section.
   * Example: "catalog-validation-rule", "notification", etc.
   */
  flag: string;

  /**
   * The error page to display if the feature flag is disabled.
   * Defaults to the `NotFoundErrorPage` app component.
   */
  errorPage?: ReactNode;

  children: ReactNode;

  fallback?: ReactNode;
};

/**
 * A boundary that only renders its children if the feature flag is enabled.
 *
 * If the feature flag is disabled, the `errorPage` element will be rendered,
 * falling back to the `NotFoundErrorPage` app component if none is provided.
 *
 * @public
 */
export function RequireFeatureFlag(
  props: RequireFeatureFlagProps,
): JSX.Element | null {
  const { flag, errorPage, children, fallback } = props;
  const configApi = useApi(configApiRef);
  const app = useApp();
  // Read from `features.<flag>` in config
  const enabled = configApi.getOptionalBoolean(`features.${flag}`) ?? false;

  if (enabled) {
    return <>{children}</>;
  }

  if (!enabled && fallback) {
    return <>{fallback}</>;
  }

  if (errorPage) {
    return <>{errorPage}</>;
  }

  const { NotFoundErrorPage } = app.getComponents();
  return <NotFoundErrorPage />;
}
